// src\pages\marketing\ProjectDetails.js
import React, { useMemo, useState, useContext, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PROJECTS_SAMPLE } from "../../data/projects.sample";
import SEO from "../../components/SEO";
import { UserContext } from "../../App";
import ShareButtons from "../../components/ShareButtons";
import BigModal from "../../components/BigModal";
import LeadForm from "../../components/LeadForm/LeadForm";
import { useTranslation } from "react-i18next";
import YouTubeEmbed from "../../components/YouTubeEmbed";
import ProjectHeroMedia from "../../components/ProjectHeroMedia";
import StickyCTA from "../../components/StickyCTA";
import { track } from "../../analytics/tracker";
import { EV } from "../../analytics/events";
import { getConsent } from "../../analytics/tracker"; 


const API_ORIGIN = "https://modularobjects.com";
const toAbs = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};


const iconByType = (t) => {
  const x = String(t || "").toLowerCase();
  if (x === "pdf") return "📄";
  if (x === "dxf") return "📐";
  if (x === "zip") return "🗜️";
  if (x === "bom") return "🧾";
  return "📁";
};




function Badge({ children }) {
  return (
    <span className="text-xs px-2 py-1 rounded bg-white/70 dark:bg-black/20 border border-gray-200 dark:border-gray-800">
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();

  const key = String(status || "").trim().toLowerCase();
  const map = {
    built: t("common.status.built"),
    in_progress: t("common.status.inProgress"),
    concept: t("common.status.concept"),
  };

  return (
    <span className="text-xs px-2 py-1 rounded bg-primary-light/10 dark:bg-primary-dark/20 border border-gray-200 dark:border-gray-800">
      {map[key] || status}
    </span>
  );
}


function intersectCount(a = [], b = []) {
  const setB = new Set((b || []).map((x) => String(x).toLowerCase()));
  return (a || []).reduce((acc, x) => {
    const key = String(x).toLowerCase();
    return acc + (setB.has(key) ? 1 : 0);
  }, 0);
}

function getRelatedProjects(allProjects, current, limit = 6) {
  const currentTags = current.tags || [];

  return (allProjects || [])
    .filter((p) => p.slug !== current.slug)
    .map((p) => {
      const sameType = p.type && current.type && p.type === current.type;
      const tagScore = intersectCount(p.tags || [], currentTags);
      const score = (sameType ? 100 : 0) + tagScore; // type je “prioritet”
      return { ...p, _score: score, _tagScore: tagScore, _sameType: sameType };
    })
    .sort((a, b) => {
      // prvo score, pa noviji
      if (b._score !== a._score) return b._score - a._score;
      return (b.year || 0) - (a.year || 0);
    })
    .slice(0, limit);
}

function buildShareUrl(project, lang = "en") {
  const prefix = lang === "sr" ? "" : ""; // ako nema prefixa, ne diraš
  return `https://modularobjects.com/share/projects/${project.slug}.html`;
}


function buildShareText(project) {
  return `${project.title} — Modular Objects`;
}

function getPinterestShareUrl(url, image, description) {
  const u = encodeURIComponent(url);
  const m = encodeURIComponent(image || "");
  const d = encodeURIComponent(description || "");
  return `https://pinterest.com/pin/create/button/?url=${u}&media=${m}&description=${d}`;
}

function getWhatsAppShareUrl(text, url) {
  const msg = encodeURIComponent(`${text}\n${url}`);
  return `https://wa.me/?text=${msg}`;
}

function getFacebookShareUrl(url) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

function getLinkedInShareUrl(url) {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

function getXShareUrl(text, url) {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
}

function canShowPrice(p) {
  const isBuilt = String(p?.status || "").trim().toLowerCase() === "built";
  const hasPrice = p?.priceTotal != null && Number.isFinite(Number(p.priceTotal));
  return isBuilt && hasPrice;
}





export default function ProjectDetails() {
  const { t, i18n } = useTranslation();

  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [activeImg, setActiveImg] = useState(0);
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);

  const [openLeadModal, setOpenLeadModal] = useState(false);
const [leadFormKey, setLeadFormKey] = useState(0);

  // ✅ language for blog route
  const uiLang = (i18n.language || "sr").toLowerCase();
const apiLang = uiLang.startsWith("sr") ? "sr" : "en";
const lang = apiLang; // ako želiš isti mapping i za blog
const langPrefix = apiLang === "en" ? "/en" : "";

  // ✅ blog index
  const [blogIndex, setBlogIndex] = useState([]);

  const FX_RSD_PER_EUR = 117;

function getDisplayPrice(priceTotal, priceCurrency, uiLang) {
  const lang = String(uiLang || "sr").toLowerCase();
  const isSR = lang.startsWith("sr");

  const total = Number(priceTotal);
  if (!Number.isFinite(total)) return null;

  const cur = String(priceCurrency || "RSD").toUpperCase();

  // locale (možeš proširiti po želji)
  const locale = isSR ? "sr-RS" : "en-US";

  if (isSR) {
    // ✅ SR uvek prikazuje RSD
    if (cur === "EUR") {
      return { amount: total * FX_RSD_PER_EUR, currency: "RSD", locale };
    }
    return { amount: total, currency: "RSD", locale };
  }

  // ✅ svi ostali jezici prikazuju EUR
  if (cur === "RSD") {
    return { amount: total / FX_RSD_PER_EUR, currency: "EUR", locale };
  }

  // ako je ponuda već u EUR (ili bilo šta što tretiraš kao EUR)
  return { amount: total, currency: "EUR", locale };
}

function formatMoney(amount, currency = "RSD", locale = "sr-RS") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;

  // EUR često lepše sa 0 decimala u prikazu projekata, ostavi 0 kao i kod tebe
  const maxFrac = 0;

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: maxFrac,
    }).format(n);
  } catch {
    return `${Math.round(n).toLocaleString(locale)} ${currency}`;
  }
}

  useEffect(() => {
    let mounted = true;

    import(`../../data/blog/${lang}/index.json`)
      .then((m) => {
        const items = Array.isArray(m.default) ? m.default : (m.default?.posts || []);
        if (mounted) setBlogIndex(items);
      })
      .catch((e) => {
        console.error("❌ blog index load error:", e);
        if (mounted) setBlogIndex([]);
      });

    return () => {
      mounted = false;
    };
  }, [lang]);

  const normalizeProject = (p) => ({
  ...p,
  cover: toAbs(p.cover || p.cover_image || p.coverImage),
  modelCode: p.modelCode ?? p.model_code ?? null,
  tags: Array.isArray(p.tags) ? p.tags : [],
  hasDocs: !!(p.hasDocs ?? p.has_docs),
  hasScan: !!(p.hasScan ?? p.has_scan),
  hasViewer: !!(p.hasViewer ?? p.has_viewer),
  hasPhotos: !!(p.hasPhotos ?? p.has_photos),
  youtubeUrl: p.youtubeUrl ?? p.youtube_url ?? p.youtube ?? null,
  instagramUrl: p.instagramUrl ?? p.instagram_url ?? p.instagram ?? null,

  // ✅ PRICE (iz backend-a)
  priceTotal: p.priceTotal ?? p.price_total ?? null,
  priceCurrency: p.priceCurrency ?? p.price_currency ?? null,
  euroRate: p.euroRate ?? p.euro_rate ?? null,

  // ako API vraća media
  media: Array.isArray(p.media)
    ? p.media.map((m) => ({ ...m, url: toAbs(m.url) }))
    : [],
});

const norm = (s) => String(s || "").toLowerCase().trim();

function inferProjectClusters(project) {
  const hay = [project?.type, ...(project?.tags || [])].map(norm).join(" ");

  const clusters = new Set();

  // design
  if (/(kitchen|kuhinj|design|dizajn|trend|style|enterijer|interior|ostrv|island)/.test(hay)) {
    clusters.add("design");
  }

  // materials
  if (/(mdf|iver|iverica|lak|paint|farb|dekor|decor|color|boje|wood|drvo|ultramat|mat)/.test(hay)) {
    clusters.add("materials");
  }

  // production
  if (/(cnc|cad|cam|proizvod|manufact|obrada|glod|bušen|drilling|edge|kant)/.test(hay)) {
    clusters.add("production");
  }

  // 3d scanning
  if (/(3d|ar|vr|scan|sken|lidar|point cloud|cloud|measure|merenj)/.test(hay)) {
    clusters.add("3d-scanning");
  }

  // fallback: ako ništa, pretpostavi design (za kuhinje uglavnom)
  if (!clusters.size) clusters.add("design");

  return [...clusters];
}

function scoreBlogForProject(blog, project, clusters) {
  let score = 0;

  if (clusters.includes(norm(blog?.cluster))) score += 100;

  // mali boost za b2c (većina tvojih projekata)
  const aud = (blog?.audience || []).map(norm);
  if (aud.includes("b2c")) score += 10;

  // noviji blogovi dobiju blagi boost
  const year = Number(blog?.year) || 0;
  score += Math.min(20, Math.max(0, year - 2024) * 5);

  return score;
}

const relatedBlogs = useMemo(() => {
  if (!project || !blogIndex?.length) return [];

  const clusters = inferProjectClusters(project);

  const scored = blogIndex
    .map((b) => ({ ...b, _score: scoreBlogForProject(b, project, clusters) }))
    .filter((b) => b._score > 0)
    .sort((a, b) => b._score - a._score);

  return scored.slice(0, 4);
}, [blogIndex, project]);


  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        // 1) detalj
       const r1 = await fetch(
        `https://modularobjects.com/marketing/api/projects/${encodeURIComponent(slug)}?lang=${apiLang}`,
        { credentials: "include" }
      );

        if (!r1.ok) throw new Error("Not found");
        const p = normalizeProject(await r1.json());
       if (mounted) {
        setProject(p);
        setActiveImg(0);

        // ✅ analytics: page view + project open
        track(EV.PAGE_VIEW, {
          ...getConsent(),
          page_url: window.location.href,
          metadata: {
            page: "project_details",
            surface: "project_details",
            project_slug: p.slug,
            project_id: p.id,
            lang: apiLang,
          },
        });
        

        track(EV.PROJECT_OPEN, {
          ...getConsent(),
          metadata: {
            surface: "project_details",
            project_slug: p.slug,
            project_id: p.id,
            lang: apiLang,
          },
        });
      }

        // 1b) ✅ docs (poseban endpoint)
        try {
          const rd = await fetch(`https://modularobjects.com/marketing/api/project-docs/${p.id}`, {
            credentials: "include",
          });

          const dj = await rd.json();
          if (mounted) {
            const arr = Array.isArray(dj) ? dj : Array.isArray(dj?.docs) ? dj.docs : [];
        const visible = user ? arr : arr.filter(d => !Number(d.requires_login));
        setDocs(visible.map(d => ({
          ...d,
          label: d.label || "Document",
          url: toAbs(d.url || d.file_path || ""),
        })));

          }
        } catch (e) {
          if (mounted) setDocs([]);
        }


        // 2) lista za related
        const r2 = await fetch(
        `https://modularobjects.com/marketing/api/projects?lang=${apiLang}`,
        { credentials: "include" }
      );

        const json2 = await r2.json(); // ✅ samo jednom
        const list = Array.isArray(json2) ? json2 : [];
        if (mounted) setAllProjects(list.map(normalizeProject));
      } catch (e) {
        console.error("❌ ProjectDetails load error:", e);
        if (mounted) {
          setProject(null);
          setAllProjects([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug, i18n.language]);

  useEffect(() => {
  if (!project?.slug) return;

  let fired = false;

  const onScroll = () => {
    if (fired) return;

    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const viewport = window.innerHeight || doc.clientHeight || 0;
    const full = doc.scrollHeight || 0;

    const maxScroll = Math.max(1, full - viewport);
    const ratio = scrollTop / maxScroll;

    if (ratio >= 0.5) {
      fired = true;

      track(EV.SCROLL_DEPTH, {
        ...getConsent(),
        metadata: {
          depth: 50,
          page: "project_details",
          surface: "project_details",
          project_slug: project.slug,
          project_id: project.id,
        },
      });

      window.removeEventListener("scroll", onScroll, { passive: true });
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });

  // u slučaju da je već skrolovano (npr. back/forward restore)
  onScroll();

  return () => window.removeEventListener("scroll", onScroll);
}, [project?.slug]);

  // ✅ HOOKS MORAJU UVEK BITI POZVANI (nema return pre ovoga)
 const galleryItems = useMemo(() => {
  const imgs = (project?.media || [])
    .filter(m => m.type === "image" && m.url)
    .map(m => ({ kind: "image", url: m.url }));

  if (!imgs.length && project?.cover) {
    imgs.push({ kind: "image", url: project.cover });
  }

  const yt = project?.youtubeUrl;
  if (yt) imgs.push({ kind: "youtube", url: yt });

  // (opciono) IG kao poseban item:
  // const ig = project?.instagramUrl;
  // if (ig) imgs.push({ kind: "instagram", url: ig });

  return imgs;
}, [project]);

const activeItem = galleryItems[activeImg];


  const photos = useMemo(() => {
  return (project?.media || []).filter(m => m.type === "image");
}, [project]);

const models = useMemo(() => {
  return (project?.media || []).filter(m => m.type === "model");
}, [project]);


  const related = useMemo(() => {
    if (!project) return [];
    return getRelatedProjects(allProjects, project, 6); // ✅ iz baze/listinga
  }, [allProjects, project]);

  // ✅ tek sad uslovni render
  if (loading) {
    return <div className="p-4">{t("projectDetails.loading")}</div>;
  }

  if (!project) {
    return (
      <div className="p-4 space-y-6">
        <section className="text-center py-16 bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800">
          <h1 className="text-2xl font-bold text-primary-light dark:text-primary-dark">
            {t("projectDetails.notFoundTitle")}
          </h1>
          <p className="text-text-light/80 dark:text-text-dark/80 mt-2">
            {t("projectDetails.notFoundSubtitle")}
          </p>
          <div className="mt-6">
            <Link
              to="/projects"
              className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-6 py-2 rounded hover:opacity-90 inline-block"
            >
              {t("projectDetails.backToProjects")}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const seoTitle = `${project.title} | Modular Objects`;
  const seoDescription =
    project.brief ||
    [...(project.problem || []), ...(project.solution || [])].slice(0, 2).join(" ");

  const seoImage = project.cover?.startsWith("http")
    ? project.cover
    : `https://modularobjects.com${project.cover?.startsWith("/") ? "" : "/"}${project.cover}`;

  const seoUrl = `https://modularobjects.com/projects/${project.slug}`;
 const shareUrl = `https://modularobjects.com/marketing/api/projects/${project.slug}/og?lang=${apiLang}`;

  const youtubeUrl =
  project.youtubeUrl ||
  (process.env.NODE_ENV === "development"
    ? "https://www.youtube.com/watch?v=8XYbV7QYP2c"
    : null);

  // ✅ PRICE display rule (isto kao u listingu)
const showPrice = canShowPrice(project);


  return (
    <>
      <SEO
  type="website"
  lang={lang}
  title={seoTitle}
  description={seoDescription}
  image={seoImage}
  url={`https://modularobjects.com${langPrefix}/projects/${project.slug}`}
   />

      <div className="p-4 space-y-10">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate(-1)}
            className="bg-primary-light/10 dark:bg-primary-dark/20 text-text-light dark:text-text-dark px-4 py-2 rounded border border-gray-200 dark:border-gray-800 hover:opacity-90"
          >
            ← {t("projectDetails.back")}
          </button>

          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={project.status} />
            {project.hasScan && <Badge>{t("common.badges.scan")}</Badge>}
{project.modelCode && <Badge>{t("common.badges.viewer")}</Badge>}
{docs.length > 0 && <Badge>{t("common.badges.docs")}</Badge>}
{photos.length > 0 && <Badge>{t("common.badges.photos")}</Badge>}
{models.length > 0 && <Badge>{t("common.badges.model")}</Badge>}

          </div>
        </div>

        {/* HERO */}
        <section className="bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">

 {/* LEFT */}
<div className="bg-black/5 dark:bg-white/5">
  {/* ✅ wrapper samo za glavnu sliku */}
  <div className="relative">
  {activeItem?.kind === "youtube" ? (
  <div className="w-full aspect-video bg-black">
    <YouTubeEmbed url={activeItem.url} title={project.title} />
  </div>
) : (
    <img
      src={activeItem?.url}
      alt={project.title}
      className="
        w-full
        h-[380px]
        sm:h-[520px]
        lg:h-[620px]
        xl:h-[720px]
        2xl:h-[820px]
        object-contain
        bg-white dark:bg-neutral-900
      "
      loading="lazy"
    />
  )}

  {showPrice && activeItem?.kind !== "youtube" && (
    <div className="absolute bottom-3 right-3">
      <div
        className="
          bg-gradient-to-r from-black/85 to-black/50
          text-primary-light
          px-6 py-3
          rounded-xl
          text-xl md:text-2xl
          font-bold
          shadow-2xl
        "
      >
       {(() => {
  const dp = getDisplayPrice(project.priceTotal, project.priceCurrency || "RSD", i18n.language);
  if (!dp) return null;
  return formatMoney(dp.amount, dp.currency, dp.locale);
})()}
      </div>
    </div>
  )}
</div>


  {/* thumbnails ostaju ispod */}
  {galleryItems.length > 1 && (
  <div className="p-3 flex gap-2 overflow-auto">
    {galleryItems.map((it, idx) => (
      <button
        key={`${it.kind}-${it.url}-${idx}`}
        onClick={() => setActiveImg(idx)}
        className={`shrink-0 rounded border ${
          idx === activeImg
            ? "border-primary-light dark:border-primary-dark"
            : "border-gray-200 dark:border-gray-800"
        }`}
        title={it.kind === "youtube" ? "Video" : ""}
      >
        {it.kind === "youtube" ? (
          <div className="h-16 w-24 rounded bg-black text-white flex items-center justify-center text-xs font-semibold">
            ▶ VIDEO
          </div>
        ) : (
          <img src={it.url} alt="" className="h-16 w-24 object-cover rounded" />
        )}
      </button>
    ))}
  </div>
)}
</div>





           {/* RIGHT */}
  <div className="p-6 space-y-4">
    {/* actions (viewer/models/docs) */}
    <div className="flex flex-col gap-3">




      {project.modelCode ? (
        <div className="flex flex-wrap gap-3">
          <a
            href={`https://modularobjects.com/demo3d/${encodeURIComponent(project.modelCode)}`}
            onClick={(e) => {
  e.preventDefault();

  track(EV.CTA_CLICK, {
    ...getConsent(),
    metadata: {
      cta: "open_viewer",
      page: "project_details",
      surface: "project_details",
      project_slug: project.slug,
      project_id: project.id,
      model_code: project.modelCode,
    },
  });

  track(EV.VIEWER_OPEN, {
    ...getConsent(),
    metadata: {
      surface: "project_details",
      project_slug: project.slug,
      project_id: project.id,
      model_code: project.modelCode,
    },
  });

  setTimeout(() => {
    window.location.href = `https://modularobjects.com/demo3d/${encodeURIComponent(project.modelCode)}`;
  }, 120);
}}
            className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90 inline-block"
          >
            ▶ {t("projectDetails.openViewer")}
          </a>

          <a
            href={`https://modularobjects.com/demo/3dmodels/Modular_Objects_Quotation_${encodeURIComponent(project.modelCode)}.pdf`}
            target="_blank"
            rel="noreferrer"
            className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90 inline-block"
          >
            {t("projectDetails.quote")}
          </a>

          {project.instagramUrl && (
  <a
    href={project.instagramUrl}
    target="_blank"
    rel="noreferrer"
    className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-5 py-2 rounded hover:opacity-90 inline-block"
  >
    📷 View on Instagram
  </a>
)}

          {/* <a
            href={`https://modularobjects.com/demo/3dmodels/materials_${encodeURIComponent(project.modelCode)}.json`}
            target="_blank"
            rel="noreferrer"
            className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90 inline-block"
          >
            Materials (debug)
          </a>

          <a
            href={`https://modularobjects.com/demo/3dmodels/offer_${encodeURIComponent(project.modelCode)}.json`}
            target="_blank"
            rel="noreferrer"
            className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90 inline-block"
          >
            Offer (debug)
          </a> */}
          
        </div>
      ) : null}

      {models.length ? (
        <div className="flex flex-wrap gap-2">
          {models.map((m) => (
            <a
              key={m.id}
              href={toAbs(m.url)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded border px-3 py-2"
            >
              🧩 {m.title || "3D model"}
            </a>
          ))}
        </div>
      ) : null}

      {docs.length ? (
  <div className="flex flex-wrap gap-2">
    {docs.map((d) => (
      <a
  key={d.id}
  href={toAbs(d.url || d.file_path)}
  target="_blank"
  rel="noreferrer"
  onClick={() => {
    track(EV.DOWNLOAD_START, {
      ...getConsent(),
      metadata: {
        surface: "project_details_docs",
        project_slug: project.slug,
        project_id: project.id,
      },
    });
  }}
  className="inline-flex items-center gap-2 rounded border px-3 py-2"
>
        {iconByType(d.type)} {d.label || "Document"}
        <span className="text-xs opacity-60">({(d.type || "").toUpperCase()})</span>
      </a>
    ))}
  </div>
) : null}


    </div>





    {/* text */}
    <h1 className="text-3xl font-bold text-primary-light dark:text-primary-dark">
      {project.title}
    </h1>

    <p className="text-text-light/80 dark:text-text-dark/80">
      {project.location} • {project.year} • {project.type}
    </p>

    {project.brief && (
      <p className="text-lg text-text-light dark:text-text-dark">{project.brief}</p>
    )}

    <div className="flex flex-wrap gap-2">
      {(project.tags || []).map((tag) => (
        <Badge key={tag}>{tag}</Badge>
      ))}
    </div>


 {/* SHARE */}
<ShareButtons
  kind="project"
  lang={lang}
  post={{
    title: project.title,
    description: seoDescription,
    image: seoImage,
    tags: project.tags || [],
    slug: project.slug,
  }}
/>



        <button
  type="button"
  onClick={() => {
    setOpenLeadModal(true);
    window.dataLayer?.push({
      event: "project_lead_modal_open",
      project_slug: project.slug,
      project_id: project.id,
    });
    track(EV.CTA_CLICK, {
      ...getConsent(),
    metadata: {
      cta: "project_request_quote",
      page: "project_details",
      surface: "project_details",
      project_slug: project.slug,
      project_id: project.id,
    },
  });

  track(EV.LEAD_MODAL_OPEN, {
    ...getConsent(),
    metadata: {
      cta: "project_request_quote",
      page: "project_details",
      surface: "project_details",
      project_slug: project.slug,
      project_id: project.id,
    },
  });
}}
  className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-5 py-2 rounded hover:opacity-90 inline-block"
>
  {/** ovde možeš t() ako imaš key */}
  {t("common.actions.requestQuote")}
</button>



  </div>

  

  
</div>



        </section>



       

        {/* RELATED */}
        {related?.length ? (
          <div className="space-y-3">
            <h2 className="text-xl font-semibold">{t("projectDetails.relatedTitle")}</h2> 
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
              {related.map((p) => (
    <Link
      key={p.slug}
      to={`/projects/${p.slug}`}
      className="group bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-lg transition"
    onClick={() => {
    track(EV.PROJECT_OPEN, {
      ...getConsent(),
      metadata: {
        surface: "project_details_related",
        from_project_slug: project.slug,
        project_slug: p.slug,
        project_type: p.type,
        project_status: p.status,
      },
    });
  }}
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden">
  <img
    src={toAbs(p.cover)}
    alt={p.title}
    className="w-full h-full object-cover group-hover:scale-[1.02] transition"
    loading="lazy"
  />

 {canShowPrice(p) && (
  <div className="absolute bottom-3 right-3">
    <div
      className="
        bg-gradient-to-r from-black/80 to-black/40
        text-primary-light
        px-4 py-1.5
        rounded-lg
        text-sm md:text-base
        font-semibold
        shadow-xl
      "
    >
      {(() => {
  const dp = getDisplayPrice(p.priceTotal, p.priceCurrency || "RSD", i18n.language);
  if (!dp) return null;
  return formatMoney(dp.amount, dp.currency, dp.locale);
})()}
    </div>
  </div>
)}
  
</div>


      <div className="p-3 space-y-1">
        <div className="font-semibold text-text-light dark:text-text-dark">
          {p.title}
        </div>

        <div className="text-sm text-text-light/70 dark:text-text-dark/70">
          {p.location} • {p.year}
        </div>
      </div>
    </Link>
  ))}
</div>
          </div>
        ) : null}


       {relatedBlogs?.length ? (
  <div className="space-y-6">
    <h2 className="text-xl font-semibold">{t("projectDetails.learnMore")}</h2>

    <div
      className="grid gap-6"
      style={{
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      }}
    >
      {relatedBlogs.map((b) => (
        <Link
          key={b.slug}
          to={`/${lang}/blog/${b.slug}`}
          className="block rounded-xl border hover:shadow-lg transition overflow-hidden p-5 bg-background-light dark:bg-background-dark"
          
        >
          <div className="font-semibold text-lg mb-2">
            {b.title || b.slug}
          </div>

          <div className="text-sm text-text-light/70 dark:text-text-dark/70 mb-3">
            {b.cluster ? `${b.cluster} • ` : ""}
            {b.date || ""}
          </div>

          <div className="text-sm text-primary-light dark:text-primary-dark font-medium">
            Read more →
          </div>
        </Link>
      ))}
    </div>
  </div>
) : null}


      </div>

      <BigModal
  open={openLeadModal}
  onClose={() => {
    setOpenLeadModal(false);
    setLeadFormKey(k => k + 1);
    window.dataLayer?.push({
      event: "project_lead_modal_close",
      project_slug: project.slug,
      project_id: project.id,
    });
   track(EV.LEAD_MODAL_CLOSE, {
    ...getConsent(),
  metadata: {
    cta: "project_lead_modal_close",
    page: "project_details",
    project_slug: project.slug,
    project_id: project.id,
  },
});
  }}
  title={t("common.lead.title")}
>
  <LeadForm
    key={leadFormKey}
    mailserverApi="https://modularobjects.com/mailserver"
    allowAttachments={true}
    initialMessage={`Hello! I’m interested in "${project.title}". Please contact me with an offer / next steps.`}
    context={{
      page: "project_details",
      project_id: project.id,
      project_slug: project.slug,
      project_title: project.title,
      project_url: seoUrl,
    }}
    onSuccess={({ apiResult, error }) => {

      if (error) return;
        if (!(apiResult?.ok || apiResult?.needsVerification)) return;

     track(EV.LEAD_SUBMIT_SUCCESS, {
    ...getConsent(),
    contact_id: apiResult?.contactId || apiResult?.contact_id || null,
    metadata: {
      page: "project_details",
      surface: "lead_form",
      project_slug: project.slug,
      project_id: project.id,
      needsVerification: !!apiResult?.needsVerification,
    },
  });


      // ✅ zatvori i na ok i na needsVerification
      if (apiResult?.ok || apiResult?.needsVerification) {
        window.dataLayer?.push({
          event: "project_lead_submit_success",
          project_slug: project.slug,
          project_id: project.id,
          needsVerification: !!apiResult?.needsVerification,
        });

        setTimeout(() => {
          setOpenLeadModal(false);
          setLeadFormKey(k => k + 1); // reset forme za sledeći put
        }, 1500);
      }
    }}
    conversion={() => {
      window.gtag?.("event", "conversion", {
        send_to: "AW-17534918048/_fhiCOLimJUbEKC7pqlB",
        value: 1.0,
        currency: "RSD",
      });
      window.gtag?.("event", "generate_lead", { value: 1.0, currency: "RSD" });
    }}
  />
</BigModal>


{!openLeadModal && project && (
  <StickyCTA
    onClick={() => {
      setOpenLeadModal(true);
      window.dataLayer?.push({
        event: "project_sticky_cta_click",
        project_slug: project.slug,
        project_title: project.title,
        project_id: project.id,
      });
      track(EV.CTA_CLICK, {
        ...getConsent(),
  metadata: {
    cta: "project_sticky_cta",
    page: "project_details",
    project_slug: project.slug,
    project_id: project.id,
  },
});
    }}
  />
)}

    </>
  );
}


